import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  useCallback,
  useRef,
} from 'react';
import {
  CheckSquare,
  Calendar,
  CalendarDays,
  User,
  Tag,
  Crosshair,
  LayoutGrid,
  Inbox,
  StickyNote,
  FileText,
  type LucideIcon,
} from 'lucide-react';

// ---------- Public types ----------

export interface SuggestionItem {
  id: string;
  label: string;
  /** Optional secondary text (email, role, entity_type, etc.) */
  secondary?: string;
  /** Hex color for tags, or type label for entities */
  color?: string;
  /** Whether this is a "create new" action rather than a real result */
  isCreate?: boolean;
}

export interface SuggestionListProps {
  items: SuggestionItem[];
  command: (item: SuggestionItem) => void;
  /** Visual variant: 'mention' shows accent-styled items, 'tag' shows color dots, 'entity' shows type icons */
  variant?: 'mention' | 'tag' | 'entity';
}

const ENTITY_ICONS: Record<string, LucideIcon> = {
  task: CheckSquare,
  meeting_instance: Calendar,
  meeting_series: CalendarDays,
  person: User,
  tag: Tag,
  scope: Crosshair,
  reference_doc: LayoutGrid,
  quick_capture: Inbox,
  note: StickyNote,
};

const ENTITY_COLORS: Record<string, string> = {
  task: '#6366f1',
  meeting_instance: '#f59e0b',
  meeting_series: '#f59e0b',
  person: '#3b82f6',
  tag: '#a855f7',
  scope: '#ec4899',
  reference_doc: '#22c55e',
  quick_capture: '#64748b',
  note: '#14b8a6',
};

export interface SuggestionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

// ---------- Component ----------

export const SuggestionList = forwardRef<SuggestionListRef, SuggestionListProps>(
  ({ items, command, variant = 'mention' }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const listRef = useRef<HTMLDivElement>(null);

    // Reset selection when items change
    useEffect(() => {
      setSelectedIndex(0);
    }, [items]);

    // Scroll selected item into view
    useEffect(() => {
      const container = listRef.current;
      if (!container) return;
      const active = container.querySelector('[data-active="true"]') as HTMLElement | null;
      if (active) {
        active.scrollIntoView({ block: 'nearest' });
      }
    }, [selectedIndex]);

    const selectItem = useCallback(
      (index: number) => {
        const item = items[index];
        if (item) {
          command(item);
        }
      },
      [items, command],
    );

    const upHandler = useCallback(() => {
      setSelectedIndex((prev) => (prev + items.length - 1) % items.length);
    }, [items.length]);

    const downHandler = useCallback(() => {
      setSelectedIndex((prev) => (prev + 1) % items.length);
    }, [items.length]);

    const enterHandler = useCallback(() => {
      selectItem(selectedIndex);
    }, [selectItem, selectedIndex]);

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === 'ArrowUp') {
          upHandler();
          return true;
        }
        if (event.key === 'ArrowDown') {
          downHandler();
          return true;
        }
        if (event.key === 'Enter') {
          enterHandler();
          return true;
        }
        if (event.key === 'Escape') {
          return true;
        }
        return false;
      },
    }));

    if (items.length === 0) {
      return (
        <div className="suggestion-dropdown">
          <div className="suggestion-empty">No results</div>
        </div>
      );
    }

    return (
      <div ref={listRef} className="suggestion-dropdown">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            data-active={index === selectedIndex}
            className={`suggestion-item ${index === selectedIndex ? 'is-selected' : ''}`}
            onClick={() => selectItem(index)}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            {variant === 'tag' && (
              <span
                className="suggestion-color-dot"
                style={{ backgroundColor: item.color || '#6366f1' }}
              />
            )}
            {variant === 'mention' && (
              <span className="suggestion-avatar">
                {item.label.charAt(0).toUpperCase()}
              </span>
            )}
            {variant === 'entity' && (() => {
              const entityType = item.secondary || '';
              const Icon = ENTITY_ICONS[entityType] || FileText;
              const iconColor = ENTITY_COLORS[entityType] || '#6366f1';
              return (
                <span className="suggestion-entity-icon" style={{ color: iconColor }}>
                  <Icon size={14} />
                </span>
              );
            })()}
            <span className="suggestion-content">
              <span className="suggestion-label">
                {item.isCreate ? (
                  <>
                    Create tag: <strong>{item.label}</strong>
                  </>
                ) : (
                  item.label
                )}
              </span>
              {variant === 'entity' && item.color && (
                <span className="suggestion-secondary">{item.color}</span>
              )}
              {variant !== 'entity' && item.secondary && (
                <span className="suggestion-secondary">{item.secondary}</span>
              )}
            </span>
          </button>
        ))}
      </div>
    );
  },
);

SuggestionList.displayName = 'SuggestionList';
